import { Trans } from "@lingui/macro";
import React from "react";
import cx from "classnames";
import { useProgress } from "pages/Event/hooks/utils/useProgress";
import { useProgressHelpers } from "pages/Event/hooks/utils/useProgressHelpers";
import EventProgress from "pages/Event/components/EventProgress";
import { roundToFixed } from "lib/roundToFixed";
// import { Tooltip } from "components/TooltipV2/Tooltip";
// import { TipIcon } from "components/TipIcon";
import { formatLargeAmount } from "pages/Event/utils/formatLargeAmount";
import { BigNumber } from "@ethersproject/bignumber";
import { formatUnitsPlp } from "pages/Event/utils/formatUnitsPlp";
import { formatAmount } from "pages/Event/utils/formatAmount";

type Props = React.ComponentProps<"div"> & {
  currentTier: BigNumber;
  currentAmount: BigNumber;
  tierDetails: {
    tier: BigNumber;
    rewardPerToken: BigNumber;
    estimatedApr: BigNumber;
    totalRewards: BigNumber;
    startAmount: BigNumber;
    endAmount: BigNumber;
  };
};

const EventTierCard = (props: Props) => {
  const { tierDetails } = props;

  const { progress, currentLimited } = useProgress(tierDetails.startAmount, tierDetails.endAmount, props.currentAmount);

  const { isFinished, isInProgress } = useProgressHelpers(progress);

  const wasStartedOnce = React.useMemo(() => {
    return props.currentTier.gte(tierDetails.tier);
  }, [props.currentTier, tierDetails.tier]);

  return (
    <div
      className={cx({
        "bg-gradient-to-bl from-[#6329fa] to-[#7156d8] p-[.5rem] rounded-[2.5rem] relative h-full isolate overflow-hidden":
          isInProgress,
      })}
    >
      {isInProgress && (
        <>
          <div className="absolute inset-0 rounded-[2.5rem] border pointer-events-none" />

          <div className="absolute -right-1/2 -top-1/2 bg-gradient-radial from-main to-[rgba(171,171,171,0)] pointer-events-none z-10 w-full h-full opacity-20 blur-[2rem]" />
        </>
      )}

      <div
        className={cx([
          "rounded-[2.5rem] border bg-background-5-v2-only @container/card relative overflow-hidden flex flex-col items-stretch justify-between",
          {
            "rounded-[2rem] p-[2rem] h-full ": isInProgress,
            "rounded-[2.5rem] p-[2.5rem]": !isInProgress,
          },
        ])}
      >
        <div className="relative">
          <div className="flex justify-between items-center gap-2 @[20rem]/card:gap-4 flex-col @[20rem]/card:flex-row">
            <p className="text-[2.4rem] leading-[1.4] font-extrabold font-mazzard">
              <Trans>Tier {tierDetails.tier.toNumber() + 1}</Trans>
            </p>

            {isFinished && (
              <div className="h-10 px-6 flex items-center font-medium text-[1.2rem] leading-[2.4rem] text-green bg-green/10 rounded-[.6rem]">
                <Trans>Finished</Trans>
              </div>
            )}

            {isInProgress && (
              <div className="h-10 px-6 flex items-center font-medium text-[1.2rem] leading-[2.4rem] text-warning bg-warning/10 rounded-[.6rem]">
                <Trans>{roundToFixed(progress * 100, 2)}% Filled</Trans>
              </div>
            )}

            {!isFinished && !isInProgress && (
              <div className="h-10 px-6 flex items-center font-medium text-[1.2rem] leading-[2.4rem] text-inactive bg-white/5 rounded-[.6rem]">
                <Trans>Upcoming</Trans>
              </div>
            )}
          </div>

          {(isFinished || wasStartedOnce) && (
            <p className="text-inactive text-[1.2rem] leading-[1.5] mb-10">
              {formatLargeAmount(formatUnitsPlp(tierDetails.startAmount))} PLP -&nbsp;
              {formatLargeAmount(formatUnitsPlp(tierDetails.endAmount))} PLP
            </p>
          )}
          {wasStartedOnce && (
            <div className="mt-2">
              <div className="flex justify-between gap-4 text-[1.2rem] leading-[1.5]">
                <div>{formatAmount(formatUnitsPlp(currentLimited))} PLP</div>

                <span>{formatAmount(formatUnitsPlp(tierDetails.endAmount))} PLP</span>
              </div>

              <EventProgress progress={progress} className="mt-[0.8rem]" />
            </div>
          )}

          {!wasStartedOnce && (
            <div>
              <p className="text-inactive text-[1.2rem] leading-[1.5] ">
                <Trans>Target Amount</Trans>
              </p>

              <p className="mt-[.5rem] text-[2.4rem] leading-[1.4] font-extrabold font-mazzard text-[#d4c5fd] mb-8">
                {formatLargeAmount(formatUnitsPlp(tierDetails.startAmount))} -&nbsp;
                {formatLargeAmount(formatUnitsPlp(tierDetails.endAmount))} PLP
              </p>
            </div>
          )}
        </div>

        <div className="mt-[1.5rem] py-[1.2rem] px-[2rem] border rounded-[2rem]">
          <div>
            <div className="text-inactive text-[1.2rem] leading-[1.5]">
              <Trans>Reward</Trans>
            </div>

            <div className="mt-[.4rem] flex justify-between">
              <div className="flex flex-col @[20rem]/card:inline">
                <span className="text-[2.4rem] leading-[1.4] font-extrabold font-mazzard text-[#e1d9fb] align-middle pr-[.9rem] ">
                  {formatAmount(formatUnitsPlp(tierDetails.rewardPerToken))} PALM
                </span>

                <span className="text-[1.2rem] align-middle leading-[1.5]">for 1 PLP</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-[1.5rem] py-[1.2rem] px-[2rem] border rounded-[2rem]">
          <ul className="flex flex-col @[30rem]/card:grid grid-cols-[1fr,1px,1fr] gap-[1.2rem]">
            {/* <li className="flex flex-col gap-[.1rem]">
              <div className="flex gap-2 items-center">
                <div className="text-inactive text-[1.2rem] leading-[1.5]">
                  <Trans>Estimated APR</Trans>
                </div>

                <Tooltip
                  renderContent={() =>
                    t`APR is calculated on the amount of PLP that is staked. APR = ((Reward in PALM) / Staked PLP) * 100`
                  }
                >
                  <TipIcon />
                </Tooltip>
              </div>

              <p className="text-[1.8rem] leading-[1.5] font-medium">
                {formatAmount(tierDetails.estimatedApr.toNumber())}%
              </p>
            </li> 

         <div className="border-r hidden @[30rem]/card:block" /> */}

            <li className="flex flex-col gap-[.1rem]">
              <div className="text-inactive text-[1.2rem] leading-[1.5]">
                <Trans>Total Rewards</Trans>
              </div>

              <p className="text-[1.8rem] leading-[1.5] font-medium">
                {formatLargeAmount(formatUnitsPlp(tierDetails.totalRewards))} PALM
              </p>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EventTierCard;
